/* eslint-disable @typescript-eslint/naming-convention -- headers */
import { RestError } from '@azure/ms-rest-js';
import type { EventData as ErrorEventData } from '@ori-events/subscription-error';
import type { EventData } from '@ori-events/subscription-saved';
import { Button, styled, Typography } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import { axios } from '@ori/axios';
import { getLogger, LogLevel } from '@ori/checkout-utils';
import type { GlobalCustomersSubscriptionInstructionDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import applyCaseMiddleware from 'axios-case-converter';
import { useCallback, useContext, useState } from 'react';

import { FormattedMessage } from '../../../components/FormattedMessage';
import { APP_NAME } from '../../../constants';
import { stateContext, wizardContext } from '../../../contexts';
import { useConfiguration } from '../../../contexts/configurationContext';
import { ECustomerTypeSelection, EWizardType } from '../../../types';
import { getSafeUrl } from '../../../utils/getSafeUrl';
import { CustomerSelectionForm } from './CustomerSelectionForm';

const logError = getLogger(APP_NAME, LogLevel.Error);

const StyledCustomerSelection = styled('div')(({ theme }) => ({
  display: 'grid',
  rowGap: theme.spacing(1),
  maxHeight: '60vh',
  marginTop: theme.spacing(3),
  width: '100%',
  overflow: 'visible',
}));

const client = applyCaseMiddleware(axios.create(), {
  // preserve keys in translations that represent locales, e.g. en-GB, ka-GE
  preservedKeys: (input) => /^[A-Za-z]{2}-[A-Za-z]{2}$/.test(input),
  caseFunctions: {
    // overrides built-in snake function to keep the keys of outcoming data in camelCase
    snake: (input) => input.replaceAll(/[^\dA-Za-z]+(.)/g, (_, char: string) => char.toUpperCase()),
  },
});

export function CustomerSelection() {
  const { getTestIdProps } = useTestId();
  const { isCustomerSelectionValid, selectedPackage, newCustomer, existingCustomer, selectedCustomerType } =
    useContext(stateContext);
  const { onClose, onSave, onError, handleOpenFeedback, productCode } = useContext(wizardContext);
  const { customerId, globalApiUrl, placement, tenant, uriTemplate } = useConfiguration();
  const [isDisabled, setIsDisabled] = useState(false);

  const { formattedToken } = useToken();

  const customerIdToPost =
    selectedCustomerType === ECustomerTypeSelection.ExistingCustomer ? existingCustomer?.endConsumerId : null;

  const postSubscription = useCallback(async () => {
    setIsDisabled(true);

    const subscriptionInstructionsToPost: GlobalCustomersSubscriptionInstructionDto = {
      newConsumer: newCustomer
        ? {
            firstName: newCustomer.firstName,
            lastName: newCustomer.surname,
          }
        : undefined,
      existingConsumerId: customerIdToPost ?? undefined,
      packageId: selectedPackage ? selectedPackage.packageId : 0,
      status: 'Finished',
    };

    try {
      const subscriptionInstructionsUrl = getSafeUrl(`/customers/${customerId}/subscriptionInstructions`, globalApiUrl);
      const { data } = await client.post<GlobalCustomersSubscriptionInstructionDto>(
        subscriptionInstructionsUrl,
        { ...subscriptionInstructionsToPost },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-placement': placement,
            'x-referer': uriTemplate,
            'x-tenant-context': tenant,
            authorization: formattedToken,
          },
        },
      );

      const detail: EventData = {
        type: EWizardType.Subscription,
        productCode,
        subscriptionId: Number(data.newSubscriptionId),
      };

      onSave({ detail });
      onClose();
      handleOpenFeedback();
    } catch (error: unknown) {
      logError('postSubscription error: ', error);
      if (error instanceof RestError) {
        const detail: ErrorEventData = {
          type: EWizardType.Subscription,
          error: {
            code: Number(error.response?.status),
            message: String(error.body),
          },
        };
        onError({ detail });
        onClose();
      }
    }
  }, [
    newCustomer,
    customerIdToPost,
    selectedPackage,
    customerId,
    globalApiUrl,
    placement,
    uriTemplate,
    tenant,
    formattedToken,
    productCode,
    onSave,
    handleOpenFeedback,
    onClose,
    onError,
  ]);

  return (
    <StyledCustomerSelection {...getTestIdProps(`customer-selection`)}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold' }}
      >
        <FormattedMessage id="For" />
      </Typography>
      <CustomerSelectionForm />
      <Button
        fullWidth
        color="primary"
        variant="contained"
        disabled={!isCustomerSelectionValid || isDisabled}
        sx={{ marginTop: 2 }}
        onClick={postSubscription}
      >
        <FormattedMessage id="Save" />
      </Button>
    </StyledCustomerSelection>
  );
}
